import {
  useNumberFormatter,
  usePercentFormatter,
} from '@mint-lib/formatting-hooks';
import { useTranslation } from '@mint-lib/i18n';
import { Information, WarningAltFilled } from '@mint-lib/icons';
import {
  Button,
  Card,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  typographyVariants,
} from '@mint-lib/tw-components';
import { ProgressLinear } from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';
import { Chat } from '@mint-sdk/api-ai-assistant';

const STORAGE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const;

type StorageUnit = (typeof STORAGE_UNITS)[number];

type StorageUsage = {
  value: number;
  metric: StorageUnit;
};

const formatBytes = (bytes = 0): StorageUsage => {
  const KB = 1024;

  if (bytes === 0)
    return {
      value: 0,
      metric: 'B',
    };

  const i = Math.floor(Math.log(bytes) / Math.log(KB));
  const value = bytes / KB ** i;

  return {
    value,
    metric: STORAGE_UNITS[i],
  };
};

const getStorageUsage = ({
  usedBytes,
  limitBytes,
}: {
  usedBytes?: number;
  limitBytes?: number;
}): {
  used: StorageUsage;
  storage: StorageUsage;
  isOverflow: boolean;
  isReached: boolean;
} => {
  return {
    used: formatBytes(usedBytes),
    storage: formatBytes(limitBytes),
    isOverflow:
      usedBytes !== undefined &&
      limitBytes !== undefined &&
      usedBytes > limitBytes,
    isReached:
      usedBytes !== undefined &&
      limitBytes !== undefined &&
      usedBytes === limitBytes,
  };
};

const defaultOptions = {
  options: {
    minDecimals: 0,
    maxDecimals: 2,
  },
};

const FileSystemUsage = ({
  onOpenRules,
  storageData,
  isLoading,
  isError,
}: {
  onOpenRules: () => void;
  storageData?: Chat.StorageUsageType;
  isLoading: boolean;
  isError: boolean;
}) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  const { format } = usePercentFormatter();
  const { format: numericFormat } = useNumberFormatter();

  const { limitBytes, usedBytes, usedPercent } = storageData ?? {};

  const storageUsage = getStorageUsage({ limitBytes, usedBytes });

  return (
    <Card
      className="flex w-full gap-2"
      contentClassName="flex flex-row w-full justify-between items-center"
    >
      {isLoading ? (
        <Skeleton className="flex h-8 w-100" />
      ) : (
        <div className="flex shrink-0 flex-row items-center gap-2">
          <Tooltip>
            <TooltipTrigger>
              <ProgressLinear className="h-1 w-70" value={usedPercent} />
            </TooltipTrigger>
            <TooltipContent>
              {isError
                ? t('Temporary unavailable')
                : format(usedPercent ?? 0, defaultOptions.options)?.formatted}
            </TooltipContent>
          </Tooltip>

          {isError ? (
            <WarningAltFilled
              className="fill-error-active"
              data-testid={'storage-usage-error'}
            />
          ) : (
            <span
              className={cn(
                'flex w-full',
                typographyVariants({
                  variant: 'caption1',
                  className: 'text-text-secondary',
                }),
              )}
            >
              {t('{{used}} of {{storage}} used', {
                used: `${
                  numericFormat(storageUsage.used.value, defaultOptions)
                    ?.formatted
                }${storageUsage.used.metric}`,
                storage: `${numericFormat(storageUsage.storage.value, defaultOptions)?.formatted}${
                  storageUsage.storage.metric
                }`,
              })}
            </span>
          )}
        </div>
      )}
      <Button
        variant="text"
        className="flex flex-row gap-2"
        data-testid="uploading-rules-btn"
        onClick={onOpenRules}
      >
        <Information />
        {t('Uploading rules')}
      </Button>
    </Card>
  );
};

export { FileSystemUsage };
